import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {Alert, Form, Input, Select} from 'antd';
import {connect} from 'react-redux';
import {Collapse} from 'react-collapse';
import Accordion from '../../../Accordion';
import Spin from '../../../Spin';
import AntdCountriesSelect from '../../../Select/AntdCountriesSelect';
import {useAccountingOptions} from '../../../../hooks';
import {formHelpers, kycHelpers, localizationHelpers, objectHelpers} from '../../../../utils/helpers';
import {validation} from '../../../../utils/validation';
import {accountingActions} from '../../../../state/actions/accounting.actions';
import {vendorsActions} from '../../../../state/actions/vendors.actions';

const {Item} = Form;

const formFieldProps = {
  size: 'large'
}

const countrySelectProps = {
  ...formFieldProps,
  ...kycHelpers.getCountrySelectProps()
}

const initialFormValues = {
  address: '',
  city: '',
  country: undefined,
  default_numbering_flow: undefined,
  email_address: '',
  name: '',
  state_province: '',
  tax_number: undefined,
  vendor_tax_code: undefined,
};

const UpdateVendorForm = React.forwardRef(({
  dispatch,

  createAccountingVendor,
  getVendor,
  syncVendor,
  updateAccountingVendor,
  updateVendor,
  updateStoredVendorInList,

  onSuccess,
  setSubmitButtonProps,
  vendor,
  ...rest
}, ref) => {
  const [t] = useTranslation(['main', 'vendors']);
  const [form] = Form.useForm();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editedVendor, setEditedVendor] = useState(null);

  const {
    numberingFlowsOptions,
    taxCodeOptions
  } = useAccountingOptions();

  const validationRules = useMemo(() => ({
    email: validation.email(t, false),
    phone: validation.phone(t, false),
    required: validation.required(t),
    vat: validation.vat(t),
  }), [t]);

  const requiredItemProps = useMemo(() => ({rules: validationRules.required, required: true}), [validationRules.required]);

  const trans = (key) => t(`vendors:${key}`);

  const formT = (key) => trans(`modal.addVendor.${key}`);

  const placeholder = (key) => formT(`placeholder.${key}`);

  useEffect(() => {
    const id = vendor?.id || vendor?.vendor_id;
    if (id) {
      getVendor({
        id,
        successCallback: (vendor) => {
          form.setFieldsValue({
            address: vendor?.address || '',
            city: vendor?.city || '',
            country: vendor?.country || undefined,
            default_numbering_flow: undefined,
            email_address: vendor?.email || '',
            name: vendor?.name || '',
            state_province: '',
            tax_number: vendor?.vat || '',
            vendor_tax_code: undefined,
          });
          setEditedVendor(vendor);
        }
      })
    }
  }, [vendor?.vendor_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const prepareVendorUpdateData = (fields) => ({
    address: fields.address || '',
    city: fields.city || '',
    country: fields.country || undefined,
    email: fields.email_address || '',
    name: fields.name,
    vat: fields.tax_number || '',
  });

  const handleSubmit = (fields) => {
    setLoading(true);
    setSubmitButtonProps({loading: true});
    fields = objectHelpers.trimAllValues(fields);

    if (fields.country) {
      fields.country_code = localizationHelpers.getCountryCodeByName(fields.country).toUpperCase();
    }

    const errorCallback = (response) => {
      formHelpers.handleFormErrors({ form, response, t, setError });
      setLoading(false);
      setSubmitButtonProps({loading: false});
    }

    const accountingVendorId = editedVendor?.accounting_vendor_id;

    const accountingVendorAction = accountingVendorId ? updateAccountingVendor : createAccountingVendor;

    updateVendor({
      id: editedVendor?.vendor_id,
      data: prepareVendorUpdateData(fields),
      successCallback: () => {
        accountingVendorAction?.({
          ...(accountingVendorId && { id: accountingVendorId }),
          data: objectHelpers.trimAllValues(fields),
          successCallback: (response) => {
            syncVendor({
              id: response.id,
              successCallback: (response) => {
                setLoading(false);
                setSubmitButtonProps({loading: false});
                onSuccess?.(response);

                updateStoredVendorInList({...editedVendor, accounting_external_reference: response?.remote_id});

                form.resetFields();
                setError(null);
              },
              errorCallback
            })
          },
          errorCallback
        });
      },
      errorCallback
    });
  }

  return (
    <Spin spinning={loading} size='small'>
      <Form
        {...rest}
        initialValues={initialFormValues}
        onFinish={handleSubmit}
        layout='vertical'
        form={form}
        ref={ref}
      >
        <Item
          {...requiredItemProps}
          label={t('name')}
          name='name'
        >
          <Input
            {...formFieldProps}
            disabled={editedVendor?.name !== ''}
            placeholder={placeholder('name')}
          />
        </Item>
        <Item
          label={t('country')}
          name='country'
        >
          <AntdCountriesSelect
            {...countrySelectProps}
            countryPropName='name'
            placeholder={placeholder('country')}
          />
        </Item>
        <Item
          label={t('vatNumber')}
          name='tax_number'
          rules={validationRules.vat}
          required
        >
          <Input
            {...formFieldProps}
            maxLength={15}
            placeholder={placeholder('vat')}
          />
        </Item>
        <Item
          {...requiredItemProps}
          label={t('taxCode')}
          name='vendor_tax_code'
        >
          <Select
            {...formFieldProps}
            placeholder={placeholder('taxCode')}
            options={taxCodeOptions}
          />
        </Item>
        <Item
          {...requiredItemProps}
          label={t('numberingFlow')}
          name='default_numbering_flow'
        >
          <Select
            {...formFieldProps}
            placeholder={placeholder('numberingFlow')}
            options={numberingFlowsOptions}
          />
        </Item>
        <Accordion
          items={[
            {
              key: '1',
              label: `${t('contact')} ${t('details')}`,
              forceRender: true,
              children: (
                <>
                  <Item
                    label={t('address')}
                    name='address'
                  >
                    <Input
                      {...formFieldProps}
                      placeholder={placeholder('address')}
                    />
                  </Item>
                  <Item
                    label={t('city')}
                    name='city'
                  >
                    <Input
                      {...formFieldProps}
                      placeholder={placeholder('city')}
                    />
                  </Item>
                  <Item
                    label={t('state')}
                    name='state_province'
                    rules={validationRules.phone}
                  >
                    <Input
                      {...formFieldProps}
                      placeholder={placeholder('state')}
                    />
                  </Item>
                  <Item
                    label={t('emailAddress')}
                    name='email_address'
                    rules={validationRules.email}
                  >
                    <Input
                      {...formFieldProps}
                      placeholder={placeholder('emailAddress')}
                    />
                  </Item>
                </>
              )
            },
          ]}
        />

        <input
          className='d-none'
          disabled={loading}
          onClick={() => form.submit()}
          type='submit'
        />

        <Collapse isOpened={Boolean(error)}>
          <Alert message={error} type='error' showIcon />
        </Collapse>
      </Form>
    </Spin>
  );
});

UpdateVendorForm.propTypes = {
  onSuccess: PropTypes.func,
  setSubmitButtonProps: PropTypes.func,
}

const mapDispatchToProps = {
  createAccountingVendor: accountingActions.createVendor,
  getVendor: vendorsActions.getVendor,
  syncVendor: accountingActions.syncVendor,
  updateAccountingVendor: accountingActions.updateVendor,
  updateVendor: vendorsActions.updateVendor,
  updateStoredVendorInList: vendorsActions.updateStoredVendorInList,
};

export default connect(null, mapDispatchToProps, null, {forwardRef: true})(UpdateVendorForm);
